import { ReactNode, useState, useEffect } from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";

interface LightboxProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function Lightbox({ isOpen, onClose, children }: LightboxProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handleClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isMounted) return null;

  if (!isOpen) return null;

  const lightboxContent = (
    <div 
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" 
      onClick={handleClick}
    >
      <button 
        onClick={onClose} 
        className="absolute top-4 right-4 text-white text-4xl hover:text-[#D4AF37] transition"
        aria-label="Close lightbox"
      >
        <X size={32} />
      </button>
      <div className="max-w-screen-lg max-h-screen">
        {children}
      </div>
    </div>
  );

  return createPortal(lightboxContent, document.body);
}

interface LightboxImageProps {
  src: string;
  alt: string;
}

export function LightboxImage({ src, alt }: LightboxImageProps) {
  return (
    <img 
      src={src} 
      alt={alt} 
      className="max-w-full max-h-[90vh]" 
    />
  );
}
